/**
 * Universal Variant Picker Web Component (<variant-picker>)
 * Drives the option selectors from snippets/product-option-select.liquid
 * (dropdown open/close or segmented row, option selection), resolves the
 * selection to a matching variant, updates the hidden variant id input, live
 * price and Add to Cart button/note state, and dispatches 'variant:change' so
 * <product-gallery> (and anything else listening) can react.
 *
 * Bundle tiles ([data-bundle-input] radios, from snippets/product-bundle-tiles.liquid)
 * are an optional, self-contained branch: if a section has no bundle_tier
 * blocks, this.bundleInputs is empty and every bundle-specific line below is
 * a no-op — same "registers but does zero work if absent" principle as any
 * other Web Component here. This used to be a second component
 * (<film-bundle-selector> / film-entwickeln.js) duplicating the option-
 * dropdown/variant-resolution logic below; merged 2026-08-05 so there's one
 * component and one 'variant:change' contract for every product, not two
 * that have to be kept in sync by hand. See
 * docs/2026-08-05-GUARDRAIL-product-buybox.md.
 *
 * The ADD-ON ([data-addon], from snippets/product-addon-toggle.liquid) is the
 * same kind of optional branch, one level up: a whole second product offered
 * inside this buy box ("Film Entwicklung dazu"). It reuses initOptionGroup for
 * its own option selectors, so there is exactly one implementation of
 * "clicking a value resolves a variant" for four surfaces — main dropdown,
 * main segments, add-on dropdown, add-on segments. Three rules hold it
 * together:
 *   - its quantity always equals the main product's (3x film → 3x developing)
 *   - its price is added to every readout: the buy box price, the ATC note,
 *     and 'variant:change'
 *   - it never rides in the <form>. A form posts one product; two line items
 *     go through cart.js's `cart:add` event instead, which posts
 *     {items: [...]} to /cart/add.js. With JS off, the toggle does nothing and
 *     the form still adds the main product on its own.
 *
 * Every label/note this component writes comes from a data-* attribute
 * rendered by Liquid (via a locale key or section setting), never a literal
 * JS string — the previous two files each hardcoded "In den Warenkorb" /
 * "Ausverkauft" as JS strings, which meant the correct server-rendered,
 * locale-aware label got silently overwritten with German the moment a
 * shopper changed a variant. See docs/2026-08-05-AUDIT-pdp-sections.md.
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variantInput = this.querySelector('[data-variant-id]');
      this.quantityInput = this.querySelector('[data-quantity-input]');
      this.optionSelects = Array.from(this.querySelectorAll('[data-option-select]'));
      this.bundleInputs = Array.from(this.querySelectorAll('[data-bundle-input]'));
      this.stepInputs = Array.from(this.querySelectorAll('[data-step-input]'));
      this.stepDataScripts = Array.from(this.querySelectorAll('[data-step-variants]'));
      this.stepProducts = {};
      this.activeRadio = null;
      this.variantsScript = this.querySelector('[data-product-variants]');
      this.variants = this.variantsScript ? JSON.parse(this.variantsScript.textContent) : [];

      if (this.variants.length === 0 && this.stepDataScripts.length === 0) return;

      // A single-variant product renders NO option selectors at all — the buy
      // box wraps them in `{% unless product.has_only_default_variant %}`.
      // Bailing out here on an empty optionSelects list (as this used to) also
      // killed the bundle tiles and the ATC price note on every such product:
      // the tiles rendered but clicking one changed nothing. Only an empty
      // variants list is a real "nothing to drive" case.
      if (this.optionSelects.length > 0) {
        this.optionSelects.sort(
          (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
        );

        this.initOptionGroup(this.optionSelects);
      }

      this.initBundleSteps();
      this.initBundleTiles();
      this.initAddon();
      this.initStickyBar();

      // After initAddon, and against the whole component rather than the main
      // product's selectors: on a single-variant product the ONLY dropdown on
      // the page is the add-on's, and registering this inside the branch above
      // (as it used to be) left that one unable to close on an outside click.
      if (this.querySelector('[data-option-toggle]')) this.initOutsideClick();

      this.updateVariantSelection();
    }

    /**
     * Wires one set of [data-option-select]-shaped elements. Shared by the main
     * product and the add-on, and by both presentations: a dropdown carries
     * [data-option-toggle] + [data-option-list] and a value readout, a
     * segmented row carries neither, and everything else — the
     * [data-option-value] buttons, the [data-option-input] the selection is
     * written to, the hint readout — is identical. The dropdown-only work is
     * therefore guarded, not required.
     */
    initOptionGroup(selects) {
      selects.forEach((selectEl) => {
        const toggleBtn = selectEl.querySelector('[data-option-toggle]');
        const listbox = selectEl.querySelector('[data-option-list]');
        const valueDisplay = selectEl.querySelector('[data-option-value-display]');
        const hintDisplay = selectEl.querySelector('[data-option-hint-display]');
        const hiddenInput = selectEl.querySelector('[data-option-input]');
        const optionBtns = Array.from(selectEl.querySelectorAll('[data-option-value]'));

        if (optionBtns.length === 0) return;

        // A listbox option reports selection through aria-selected, a radio
        // through aria-checked. Read which one Liquid rendered rather than
        // writing both — an element carrying both is invalid ARIA and reads
        // wrong to a screen reader.
        const stateAttr = optionBtns[0].hasAttribute('aria-checked') ? 'aria-checked' : 'aria-selected';

        if (toggleBtn && listbox) {
          toggleBtn.addEventListener('click', (event) => {
            event.preventDefault();
            const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
            this.closeAllDropdowns();
            if (!isExpanded) {
              toggleBtn.setAttribute('aria-expanded', 'true');
              listbox.removeAttribute('hidden');
            }
          });
        }

        optionBtns.forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            const value = btn.dataset.optionValue;
            const hint = btn.dataset.optionHint || '';

            if (hiddenInput) hiddenInput.value = value;
            if (valueDisplay) valueDisplay.textContent = value;
            if (hintDisplay) hintDisplay.textContent = hint;

            optionBtns.forEach((b) => b.setAttribute(stateAttr, b === btn ? 'true' : 'false'));

            if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
            if (listbox) listbox.setAttribute('hidden', '');

            this.updateVariantSelection();
          });
        });
      });
    }

    initBundleSteps() {
      if (this.stepInputs.length === 0) return;

      this.stepProducts = {};
      this.stepDataScripts.forEach((script) => {
        try {
          const data = JSON.parse(script.textContent);
          if (data.handle) this.stepProducts[data.handle] = data;
          if (data.id) this.stepProducts[String(data.id)] = data;
        } catch (e) {
          console.error('Error parsing step product JSON:', e);
        }
      });

      this.stepInputs.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.updateVariantSelection();
        });
      });
    }

    getActiveStep() {
      if (this.stepInputs.length === 0) return null;
      const checked = this.stepInputs.find((r) => r.checked) || this.stepInputs[0];
      if (!checked) return null;
      const handle = checked.dataset.stepHandle;
      const id = checked.dataset.stepProductId;
      const productData = this.stepProducts[handle] || this.stepProducts[id] || null;
      return {
        radio: checked,
        handle,
        id,
        customCompare: checked.dataset.stepCustomCompare || null,
        product: productData
      };
    }

    initBundleTiles() {
      if (this.bundleInputs.length === 0) return;

      const checkedRadio = this.bundleInputs.find((r) => r.checked);
      if (checkedRadio) {
        this.activeRadio = checkedRadio;
      }

      this.bundleInputs.forEach((radio) => {
        radio.addEventListener('change', () => {
          this.activeRadio = radio;
          this.updateVariantSelection();
        });
      });
    }

    closeAllDropdowns() {
      this.querySelectorAll('[data-option-toggle]').forEach((toggleBtn) => {
        toggleBtn.setAttribute('aria-expanded', 'false');
      });
      this.querySelectorAll('[data-option-list]').forEach((listbox) => {
        listbox.setAttribute('hidden', '');
      });
    }

    initOutsideClick() {
      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.closeAllDropdowns();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAllDropdowns();
      });
    }

    // -----------------------------------------------------------------------
    // Add-on ("Film Entwicklung dazu")
    // -----------------------------------------------------------------------

    initAddon() {
      this.addonRoot = this.querySelector('[data-addon]');
      if (!this.addonRoot) return;

      const addonVariantsScript = this.addonRoot.querySelector('[data-addon-variants]');
      this.addonVariants = addonVariantsScript ? JSON.parse(addonVariantsScript.textContent) : [];

      if (this.addonVariants.length === 0) {
        this.addonRoot = null;
        return;
      }

      // The add-on's own volume discount (addon_discount_tier blocks),
      // independent of the main product's bundle_tier blocks — see
      // snippets/product-addon-toggle.liquid's "THE ADD-ON HAS ITS OWN VOLUME
      // DISCOUNT" note.
      const addonTiersScript = this.addonRoot.querySelector('[data-addon-discount-tiers]');
      this.addonDiscountTiers = addonTiersScript ? JSON.parse(addonTiersScript.textContent) : [];

      this.addonSelects = Array.from(this.addonRoot.querySelectorAll('[data-addon-option-select]')).sort(
        (a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)
      );
      this.addonPanel = this.addonRoot.querySelector('[data-addon-panel]');
      this.addonSummary = this.addonRoot.querySelector('[data-addon-summary]');
      this.addonHint = this.addonRoot.querySelector('[data-addon-hint]');
      this.addonOnValue = this.addonRoot.dataset.addonOnValue;
      this.addonEnabled = this.addonRoot.dataset.addonEnabled === 'true';

      this.initOptionGroup(this.addonSelects);

      // The add-on's line quantity follows the main product's, so a typed
      // quantity has to reach the readouts too — see getQuantity().
      if (this.quantityInput) {
        this.quantityInput.addEventListener('input', () => this.updateVariantSelection());
      }

      // The toggle is a segmented-control rendered directly under the label,
      // outside any option-select wrapper, so its buttons are exactly the
      // [data-option-value] elements in this block that initOptionGroup did not
      // already claim.
      const toggleBtns = Array.from(this.addonRoot.querySelectorAll('[data-option-value]')).filter(
        (btn) => !btn.closest('[data-addon-option-select]')
      );
      toggleBtns.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.addonEnabled = btn.dataset.optionValue === this.addonOnValue;
          this.addonRoot.dataset.addonEnabled = String(this.addonEnabled);
          toggleBtns.forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));

          // The panel is NOT hidden while off: its <summary> carries the hint
          // line, which the "Nein" state still has to show. What goes away is
          // the affordance — CSS hides "Anpassen" and its chevron off
          // [data-addon-enabled], and the summary leaves the tab order here,
          // since that is the half CSS can't reach. See the snippet's own
          // "THE HINT LINE IS THE SUMMARY" note.
          if (this.addonSummary) this.addonSummary.tabIndex = this.addonEnabled ? 0 : -1;
          // Collapse on the way out, so switching back on never reveals a
          // panel the shopper did not just open.
          if (this.addonPanel && !this.addonEnabled) this.addonPanel.open = false;

          this.updateVariantSelection();
        });
      });

      this.initAddonSubmit();
      this.initAddonNudge();
    }

    /**
     * "Entwicklung direkt oben dazubuchen" — highlights the add-on toggle when
     * a shopper arrives via the Tab-3 CTA (compact tabs) or the FAQ q2 inline
     * link, both `<a href="#entwicklung-dazu">`. See
     * copy/atomic/film-kodak-ultramax-400.md §1e for the full spec.
     *
     * Listens on `document`, not on this component: the FAQ section is a
     * separate `<product-faq>`-less section further down the page, outside
     * `<variant-picker>` entirely, and a future full-width `product-tabs`
     * section would be too.
     *
     * SCROLL IS OURS, NOT THE BROWSER'S. The toggle sits near the top of a
     * long buybox rail; the browser's native anchor jump (scroll-padding-top,
     * assets/critical.css) only clears the sticky header and leaves the row
     * pinned to the top edge. `scrollIntoView({ block: 'center' })` instead
     * lands it mid-viewport, which is the point of a nudge — so the click
     * handler calls `preventDefault()` to stop the native jump from firing
     * first and fighting this one.
     *
     * THE FLASH WAITS FOR THE SCROLL — and actually waits for IT, not a
     * guessed duration. A fixed delay (this used to be a flat 450ms
     * setTimeout) is tuned for whatever distance someone tested with; the FAQ
     * q2 link sits near the page bottom, so on a long mobile page the smooth
     * scroll up to the toggle is still travelling well past 450ms and the
     * flash burns its whole run on an off-screen row. waitForScrollSettle
     * instead polls window.scrollY every frame and calls back once it has
     * stopped changing for a few frames running, so the flash starts exactly
     * when the row is actually in view regardless of how far it had to
     * travel. `scrollend` would ask this more directly but isn't reliably
     * available across the browsers this theme has to support, so this reads
     * the position itself instead. Skipped under reduced motion, where the
     * jump is instant (`behavior: 'auto'`) and there is nothing to wait for.
     *
     * Two triggers: `click` covers a shopper already on the anchor clicking
     * the same CTA again (the hash doesn't change under preventDefault, so no
     * `hashchange` would fire); `hashchange` covers arriving with that hash
     * already in the URL (deep link, no click event at all).
     */
    initAddonNudge() {
      const NUDGE_MS = 2200;
      const SETTLE_FRAMES = 6;
      const SETTLE_TIMEOUT_MS = 3000;

      const flash = () => {
        // Remove + reflow + add, so a second click restarts the animation
        // instead of no-op'ing on a class that's already set.
        this.addonRoot.classList.remove('addon-toggle--nudge');
        void this.addonRoot.offsetWidth;
        this.addonRoot.classList.add('addon-toggle--nudge');

        clearTimeout(this.nudgeTimer);
        this.nudgeTimer = setTimeout(() => {
          this.addonRoot.classList.remove('addon-toggle--nudge');
        }, NUDGE_MS);
      };

      // Calls `done` once window.scrollY has held still for SETTLE_FRAMES
      // consecutive frames (the scrollIntoView animation has finished, or
      // never needed to move at all), with SETTLE_TIMEOUT_MS as a safety net
      // in case something keeps the page moving (e.g. a slow-loading image
      // shifting layout) so the nudge always fires eventually.
      const waitForScrollSettle = (done) => {
        cancelAnimationFrame(this.nudgeSettleFrame);
        let lastY = window.scrollY;
        let stableFrames = 0;
        const start = performance.now();

        const check = () => {
          const y = window.scrollY;
          if (y === lastY) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
            lastY = y;
          }

          if (stableFrames >= SETTLE_FRAMES || performance.now() - start > SETTLE_TIMEOUT_MS) {
            done();
            return;
          }

          this.nudgeSettleFrame = requestAnimationFrame(check);
        };

        this.nudgeSettleFrame = requestAnimationFrame(check);
      };

      const trigger = () => {
        if (!this.addonRoot) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.addonRoot.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

        if (reduceMotion) {
          flash();
        } else {
          waitForScrollSettle(flash);
        }

        // preventScroll: scrollIntoView above already placed the viewport;
        // this only moves keyboard/screen-reader focus onto the toggle so Tab
        // doesn't land back at the top of the page.
        this.addonRoot.focus({ preventScroll: true });
      };

      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href="#entwicklung-dazu"]');
        if (!link) return;
        event.preventDefault();
        trigger();
      });

      window.addEventListener('hashchange', () => {
        if (window.location.hash === '#entwicklung-dazu') trigger();
      });
    }

    /**
     * Sends both line items in one request when the add-on is on.
     *
     * The listener sits on the <form> itself, not on document: a submit event's
     * target IS the form, so this runs before cart.js's document-level handler
     * and stopPropagation() reliably keeps the single-item path from also
     * firing. Registration order between the two files never comes into it.
     */
    initAddonSubmit() {
      const form = this.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      form.addEventListener('submit', (event) => {
        if (!this.addonEnabled) return;

        const mainVariant = this.getMatchedVariant();
        const addonVariant = this.getMatchedAddonVariant();

        // Fail closed, before the resolution check. updateAtc already disables
        // the button whenever the add-on can't be resolved, so an unresolved
        // add-on here means an implicit submit — and letting that reach
        // cart.js would post the main product alone, quietly dropping the line
        // item the toggle promised.
        event.preventDefault();
        event.stopPropagation();
        if (!mainVariant || !addonVariant) return;

        const quantity = this.getQuantity();
        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.setAttribute('disabled', 'disabled');

        const release = () => {
          if (submitBtn) submitBtn.removeAttribute('disabled');
        };
        // cart.js's cart:add path reports success through cart:updated and
        // failure through neither, so the timer is the only guaranteed way back
        // out of the disabled state.
        document.addEventListener('cart:updated', release, { once: true });
        setTimeout(release, 8000);

        document.dispatchEvent(
          new CustomEvent('cart:add', {
            detail: {
              items: [
                { id: mainVariant.id, quantity },
                { id: addonVariant.id, quantity }
              ]
            },
            bubbles: true
          })
        );
      });
    }

    getMatchedAddonVariant() {
      if (!this.addonRoot) return null;

      if (this.addonSelects.length === 0) return this.addonVariants[0];

      const selectedValues = this.addonSelects.map((selectEl) => {
        const hiddenInput = selectEl.querySelector('[data-option-input]');
        return hiddenInput ? hiddenInput.value : '';
      });

      return this.addonVariants.find((variant) =>
        selectedValues.every((value, index) => variant.options[index] === value)
      );
    }

    // `totalCents` is the add-on's DISCOUNTED total, already computed once in
    // updateVariantSelection — this only formats it, it does not re-derive
    // the discount, so there is exactly one place that decides what the
    // add-on actually costs.
    updateAddonHint(addonVariant, quantity, totalCents) {
      if (!this.addonHint) return;

      // Plural only above one unit, and only while on: "Nein" says nothing
      // about a quantity. Liquid already defaulted hintOnPlural to hintOn, so
      // a block that sets no plural copy behaves exactly as before.
      let template = this.addonHint.dataset.hintOff;
      if (this.addonEnabled) {
        template = quantity > 1 ? this.addonHint.dataset.hintOnPlural : this.addonHint.dataset.hintOn;
      }
      if (!template) {
        this.addonHint.textContent = '';
        return;
      }

      const unitPriceCents = quantity ? Math.round(totalCents / quantity) : 0;
      this.addonHint.textContent = template
        .replace('[price]', this.formatMoney(totalCents))
        .replace('[unit_price]', this.formatMoney(unitPriceCents))
        .replace('[quantity]', quantity);
    }

    // -----------------------------------------------------------------------

    getSelectedOptionValues() {
      return this.optionSelects.map((selectEl) => {
        const hiddenInput = selectEl.querySelector('[data-option-input]');
        return hiddenInput ? hiddenInput.value : '';
      });
    }

    formatMoney(cents) {
      return `CHF ${(cents / 100).toFixed(2)}`;
    }

    // The one quantity every readout and both line items agree on.
    //
    // With bundle tiles it is the checked tier, or 1 when none is. With a plain
    // <input type="number"> it is what the shopper typed — but ONLY when there
    // is an add-on to multiply. Without one, this buy box has always shown the
    // unit price beside a free-typed quantity, and quietly turning that into a
    // line total on every product is not this change's call.
    getQuantity() {
      if (this.bundleInputs.length) {
        const checkedRadio = this.bundleInputs.find((radio) => radio.checked);
        return checkedRadio ? parseInt(checkedRadio.dataset.quantity, 10) : 1;
      }

      if (!this.addonRoot || !this.quantityInput) return 1;
      const typed = parseInt(this.quantityInput.value, 10);
      return Number.isFinite(typed) && typed > 0 ? typed : 1;
    }

    // One rounding formula for both discount schedules the buy box can have:
    // the main product's bundle_tier blocks and the add-on's own
    // addon_discount_tier blocks. Callers resolve their own percent — the
    // main product's from whichever [data-bundle-input] radio is checked
    // (getBundleDiscountPercent), the add-on's from a quantity match against
    // this.addonDiscountTiers (getAddonDiscountPercent) — because the two
    // schedules are independent settings that only coincide in today's data.
    calculateEffectivePrice(basePrice, qty, discountPercent) {
      // Per unit and rounded down, like Shopify's own percentage discount.
      return (basePrice - Math.floor((basePrice * discountPercent) / 100)) * qty;
    }

    getBundleDiscountPercent() {
      const checkedRadio = this.bundleInputs.find((radio) => radio.checked);
      return checkedRadio ? parseFloat(checkedRadio.dataset.discountPercent || '0') : 0;
    }

    // Exact match only, same as the bundle tiles themselves: quantity is
    // always exactly 1, or exactly whichever bundle tile is checked, never a
    // typed number that happens to fall between two tiers (see getQuantity).
    getAddonDiscountPercent(qty) {
      const tier = this.addonDiscountTiers.find((t) => t.quantity === qty);
      return tier ? tier.discount_percent : 0;
    }

    // With no option selectors there is nothing to resolve: the variant is
    // whichever one the hidden [data-variant-id] input was server-rendered with.
    getMatchedVariant() {
      const activeStep = this.getActiveStep();
      const currentVariants =
        activeStep && activeStep.product && activeStep.product.variants && activeStep.product.variants.length
          ? activeStep.product.variants
          : this.variants;

      const currentOptionNames =
        activeStep && activeStep.product && activeStep.product.options
          ? activeStep.product.options
          : null;

      if (this.optionSelects.length === 0) {
        const currentId = this.variantInput ? Number(this.variantInput.value) : null;
        return currentVariants.find((variant) => variant.id === currentId) || currentVariants[0];
      }

      // Build map of { [lowercaseOptionName]: selectedValue }
      const selectedMap = {};
      this.optionSelects.forEach((selectEl) => {
        const optName = (selectEl.dataset.optionName || '').toLowerCase().trim();
        const hiddenInput = selectEl.querySelector('[data-option-input]');
        const val = hiddenInput ? hiddenInput.value : '';
        if (optName) {
          selectedMap[optName] = val;
        }
      });

      // Name-based mapping across products if option names are available
      if (currentOptionNames && currentOptionNames.length > 0) {
        const matched = currentVariants.find((variant) => {
          return currentOptionNames.every((name, index) => {
            const key = name.toLowerCase().trim();
            let targetVal = selectedMap[key];
            if (!targetVal) {
              const matchedKey = Object.keys(selectedMap).find(
                (k) => k.startsWith(key) || key.startsWith(k)
              );
              if (matchedKey) targetVal = selectedMap[matchedKey];
            }
            if (!targetVal) return true;
            const optKey = `option${index + 1}`;
            return (variant.options && variant.options[index] === targetVal) || variant[optKey] === targetVal;
          });
        });
        if (matched) return matched;
      }

      const selectedValues = this.getSelectedOptionValues();
      return (
        currentVariants.find((variant) =>
          selectedValues.every((value, index) => {
            if (!value) return true;
            if (variant.options && variant.options[index] !== undefined) {
              return variant.options[index] === value;
            }
            const optKey = `option${index + 1}`;
            return variant[optKey] === value;
          })
        ) ||
        currentVariants.find((v) => v.available) ||
        currentVariants[0]
      );
    }

    updateVariantSelection() {
      const matchedVariant = this.getMatchedVariant();

      if (!matchedVariant) return;

      if (this.variantInput) this.variantInput.value = matchedVariant.id;

      const activeStep = this.getActiveStep();
      const qty = this.getQuantity();
      let totalPriceCents = matchedVariant.price * qty;
      let unitPriceCents = matchedVariant.price;
      // Per-unit variant.compare_at_price by default; a multi-unit purchase
      // compares against the full (undiscounted) multi-unit total instead — the
      // two are different things and must not be mixed.
      let compareAtPriceCents = matchedVariant.compare_at_price;
      if (qty > 1) compareAtPriceCents = matchedVariant.price * qty;

      if (activeStep && activeStep.customCompare) {
        const parsed = parseFloat(activeStep.customCompare.replace(/[^\d.]/g, ''));
        if (!isNaN(parsed) && parsed > 0) {
          compareAtPriceCents = Math.round(parsed * 100);
        }
      }

      if (this.stepInputs.length) {
        this.updateStepTileReadouts();
      }

      if (this.bundleInputs.length) {
        totalPriceCents = this.calculateEffectivePrice(matchedVariant.price, qty, this.getBundleDiscountPercent());

        if (this.quantityInput) this.quantityInput.value = qty;
        this.updateBundleTileReadouts(matchedVariant.price);
      }

      // The add-on gets its OWN volume discount at the same quantity (see
      // getAddonDiscountPercent) — it is a per-item service billed at the same
      // multi-unit rate Film Entwickeln's own product page would charge for
      // that many units, not a flat per-unit price regardless of quantity.
      // Compare-at adds the FULL undiscounted add-on total, not the discounted
      // one: the struck-through number has to stay the honest "before either
      // discount" price, so the gap it shows against totalPriceCents is the
      // combined saving of both schedules, not just the main product's.
      const addonVariant = this.addonEnabled ? this.getMatchedAddonVariant() : null;
      const addonFullTotalCents = addonVariant ? addonVariant.price * qty : 0;
      const addonTotalCents = addonVariant
        ? this.calculateEffectivePrice(addonVariant.price, qty, this.getAddonDiscountPercent(qty))
        : 0;
      totalPriceCents += addonTotalCents;
      if (compareAtPriceCents > 0) compareAtPriceCents += addonFullTotalCents;
      unitPriceCents = Math.round(totalPriceCents / qty);

      // "Switched on and actually sellable". An add-on that is on but resolves
      // to nothing — an unavailable variant, or an option combination the
      // add-on product has no variant for — has to block the button rather
      // than be dropped: /cart/add.js would reject the whole request anyway,
      // and the alternative (submitting just the main product) silently
      // removes a line item the shopper deliberately added.
      const addonReady = !this.addonEnabled || Boolean(addonVariant && addonVariant.available);

      if (this.addonRoot) this.updateAddonHint(addonVariant, qty, addonTotalCents);

      this.updatePrice(matchedVariant, totalPriceCents, compareAtPriceCents, addonReady);
      this.updateAtc(matchedVariant, qty, unitPriceCents, totalPriceCents, addonReady, compareAtPriceCents);
      this.updateStickySelection();

      this.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: {
            variant: matchedVariant,
            variantId: matchedVariant.id,
            quantity: qty,
            price: totalPriceCents,
            compareAtPrice: compareAtPriceCents,
            unitPriceCents,
            available: matchedVariant.available,
            addonVariant,
            addonPrice: addonTotalCents,
            featuredMediaId: matchedVariant.featured_media ? matchedVariant.featured_media.id : null,
            stepImage: matchedVariant.step_image || null
          },
          bubbles: true,
          composed: true
        })
      );
    }

    initStickyBar() {
      this.stickyRoot = this.querySelector('[data-sticky-atc]');
      if (!this.stickyRoot) return;

      this.stickyButton = this.stickyRoot.querySelector('[data-sticky-atc-submit]');
      this.stickyLabel = this.stickyRoot.querySelector('[data-sticky-atc-label]');
      this.stickyNote = this.stickyRoot.querySelector('[data-sticky-atc-note]');
      this.stickyChoices = this.stickyRoot.querySelector('[data-sticky-choices]');
      this.stickyAddonButtons = Array.from(this.stickyRoot.querySelectorAll('[data-sticky-addon-btn]'));

      // 1. Shows once the main button has scrolled away upwards. On scroll, not
      // an IntersectionObserver: a fast fling or a jump can pass the button
      // without it ever intersecting, and then no event would fire.
      const mainAtcButton = this.querySelector('[data-atc-button]');
      if (mainAtcButton) {
        let queued = false;
        const check = () => {
          queued = false;
          this.setStickyActive(mainAtcButton.getBoundingClientRect().bottom < 0);
        };
        const queue = () => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(check);
        };
        window.addEventListener('scroll', queue, { passive: true });
        window.addEventListener('resize', queue, { passive: true });
        check();
      }

      // 2. "ändern": back to the buybox, the one place options are chosen
      this.stickyRoot.querySelector('[data-sticky-edit]')?.addEventListener('click', () => this.scrollToOptions());

      // The cart popup opens above the bar: it reads this height.
      if (window.ResizeObserver) {
        new ResizeObserver(() => this.reportStickyHeight()).observe(this.stickyRoot);
      }

      // 3. Wire sticky addon toggle (Nein / Ja)
      if (this.stickyAddonButtons.length && this.addonRoot) {
        this.stickyAddonButtons.forEach((btn) => {
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            const val = btn.dataset.stickyAddonBtn;
            const mainToggleBtn = Array.from(
              this.addonRoot.querySelectorAll('[data-option-value]')
            ).find((b) => !b.closest('[data-addon-option-select]') && b.dataset.optionValue === val);

            if (mainToggleBtn) {
              mainToggleBtn.click();
            }
          });
        });
      }

      // 4. Wire sticky ATC submit click. With nothing to buy in this selection
      // it says "Auswahl ändern" and goes where that can be done.
      if (this.stickyButton) {
        this.stickyButton.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.querySelector('[data-atc-button]')?.disabled) {
            this.scrollToOptions();
            return;
          }

          const mainButton = this.querySelector('[data-atc-button]');
          if (mainButton) {
            mainButton.click();
          } else {
            const form = this.querySelector('form[action*="/cart/add"]');
            if (form) {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          }
        });
      }
    }

    setStickyActive(active) {
      if (active === this.stickyRoot.classList.contains('is-active')) return;
      this.stickyRoot.classList.toggle('is-active', active);
      this.stickyRoot.setAttribute('aria-hidden', String(!active));
      this.stickyRoot.toggleAttribute('inert', !active);
      this.reportStickyHeight();
    }

    reportStickyHeight() {
      const active = this.stickyRoot.classList.contains('is-active');
      const height = active ? this.stickyRoot.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--sticky-atc-offset', active ? `${height}px` : '0px');
    }

    /** The buybox's options in view, marked for a moment, focus on the first. */
    scrollToOptions() {
      const target = this.querySelector('.buybox__options, .bundle-tiles, .buybox__purchase');
      if (!target) return;
      const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'center' });
      target.classList.remove('is-nudged');
      void target.offsetWidth;
      target.classList.add('is-nudged');
      setTimeout(() => target.classList.remove('is-nudged'), 1200);
      const first =
        target.querySelector('[data-option-value][aria-checked="true"]') ||
        target.querySelector('[data-option-value], input:not([type="hidden"]), select');
      first?.focus({ preventScroll: true });
    }

    /** One choice as the customer reads it, same wording as the cart. */
    stickyChoiceLabel(name, value) {
      if (value === 'Für jemand anderes' || value === 'Jemand anderes') return 'Als Geschenk';
      if (/negative/i.test(name) && value === 'Ja') return 'Negative zurück';
      if (/negative/i.test(name) && value === 'Nein') return 'Nur Scan';
      return value;
    }

    updateStickySelection() {
      if (!this.stickyRoot) return;

      if (this.stickyChoices) {
        const choices = this.optionSelects
          .map((selectEl) =>
            this.stickyChoiceLabel(
              selectEl.dataset.optionName || '',
              selectEl.querySelector('[data-option-input]')?.value || ''
            )
          )
          .filter(Boolean);

        const step = this.getActiveStep();
        const quantity = this.getQuantity();
        if (step?.radio.dataset.stepLabel) choices.push(step.radio.dataset.stepLabel);
        else if (quantity > 1) choices.push(`${quantity}×`);

        this.stickyChoices.textContent = choices.join(' / ');
      }

      // Update sticky addon active state
      if (this.stickyAddonButtons && this.stickyAddonButtons.length && this.addonRoot) {
        const isEnabled = this.addonEnabled;
        this.stickyAddonButtons.forEach((btn) => {
          const isYes = btn.dataset.stickyAddonBtn === this.addonOnValue;
          const isActive = isEnabled ? isYes : !isYes;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-checked', isActive ? 'true' : 'false');
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }
    }

    updatePrice(variant, effectivePriceCents, compareAtPriceCents, addonReady) {
      const priceWrapper = this.querySelector('.price');
      const priceEl = this.querySelector('[data-price]');
      const compareEl = this.querySelector('[data-compare-price]');
      const onSale = compareAtPriceCents > effectivePriceCents;
      const soldOut = !variant.available || !addonReady;

      if (priceEl) priceEl.textContent = this.formatMoney(effectivePriceCents);

      if (compareEl) {
        compareEl.textContent = this.formatMoney(compareAtPriceCents);
        compareEl.hidden = !onSale;
      }

      if (priceWrapper) {
        priceWrapper.classList.toggle('price--on-sale', onSale);
        priceWrapper.classList.toggle('price--sold-out', soldOut);
      }
    }

    updateAtc(variant, qty, unitPriceCents, totalPriceCents, addonReady, compareAtPriceCents) {
      const button = this.querySelector('[data-atc-button]');
      const label = this.querySelector('[data-atc-label]');
      const note = this.querySelector('[data-atc-note]');

      const available = variant.available && addonReady;

      if (button) button.disabled = !available;

      if (label) {
        if (!available) {
          label.textContent = label.dataset.soldOutLabel;
        } else {
          label.textContent = qty > 1 && label.dataset.labelPlural ? label.dataset.labelPlural : label.dataset.label;
        }
      }

      if (note) {
        const template =
          qty > 1 && note.dataset.noteTemplatePlural ? note.dataset.noteTemplatePlural : note.dataset.noteTemplate;
        if (template) {
          note.textContent = template
            .replace('[price]', this.formatMoney(totalPriceCents))
            .replace('[unit_price]', this.formatMoney(unitPriceCents));
        }
      }

      const compareEl = this.querySelector('[data-atc-compare]');
      if (compareEl) {
        const onSale = compareAtPriceCents && compareAtPriceCents > totalPriceCents;
        if (onSale) {
          compareEl.textContent = this.formatMoney(compareAtPriceCents);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      // Sync sticky ATC
      if (this.stickyLabel) {
        if (!available) {
          this.stickyLabel.textContent = this.stickyLabel.dataset.editLabel;
        } else {
          this.stickyLabel.textContent =
            qty > 1 && this.stickyLabel.dataset.labelPlural
              ? this.stickyLabel.dataset.labelPlural
              : this.stickyLabel.dataset.label;
        }
      }
      if (this.stickyNote) {
        const template =
          (qty > 1 && this.stickyNote.dataset.noteTemplatePlural
            ? this.stickyNote.dataset.noteTemplatePlural
            : this.stickyNote.dataset.noteTemplate) || '[price]';
        const formattedPrice = this.formatMoney(totalPriceCents);
        const formattedUnit = this.formatMoney(unitPriceCents);
        const unitEl = this.stickyRoot ? this.stickyRoot.querySelector('[data-sticky-atc-unit]') : null;

        if (template.includes('(')) {
          const parts = template.split('(');
          const mainPart = parts[0].replace('[price]', formattedPrice).trim();
          const unitPart = '(' + parts[1].replace('[unit_price]', formattedUnit);
          this.stickyNote.textContent = mainPart;
          if (unitEl) {
            unitEl.textContent = unitPart;
            unitEl.hidden = false;
          }
        } else {
          this.stickyNote.textContent = template
            .replace('[price]', formattedPrice)
            .replace('[unit_price]', formattedUnit);
          if (unitEl) {
            unitEl.textContent = '';
            unitEl.hidden = true;
          }
        }
      }
      if (this.stickyRoot) {
        const stickyCompareEl = this.stickyRoot.querySelector('[data-sticky-atc-compare]');
        if (stickyCompareEl) {
          const onSale = compareAtPriceCents && compareAtPriceCents > totalPriceCents;
          if (onSale) {
            stickyCompareEl.textContent = this.formatMoney(compareAtPriceCents);
            stickyCompareEl.hidden = false;
          } else {
            stickyCompareEl.hidden = true;
          }
        }
      }
    }

    // Live tile readouts (price/compare/savings) recomputed from the
    // matched variant's actual price — never stored as separate hardcoded
    // numbers that could drift from it. Deliberately main-product-only: the
    // tiles advertise the film's volume discount, and folding the add-on into
    // them would make the advertised saving stop matching the percentage.
    updateBundleTileReadouts(basePrice) {
      this.bundleInputs.forEach((radio) => {
        const qty = parseInt(radio.dataset.quantity, 10);
        const discountPercent = parseFloat(radio.dataset.discountPercent || '0');
        const tile = radio.closest('.bundle-tiles__tile');
        if (!tile) return;

        const fullTotal = basePrice * qty;
        const discountedTotal = this.calculateEffectivePrice(basePrice, qty, discountPercent);
        const savingsCents = fullTotal - discountedTotal;

        const priceEl = tile.querySelector('[data-bundle-price]');
        const compareEl = tile.querySelector('[data-bundle-compare]');
        const savingEl = tile.querySelector('[data-bundle-saving]');

        if (priceEl) priceEl.textContent = this.formatMoney(discountedTotal);
        if (compareEl) {
          if (savingsCents > 0) {
            compareEl.textContent = this.formatMoney(fullTotal);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
        if (savingEl) {
          savingEl.textContent =
            savingsCents > 0 && savingEl.dataset.savingTemplate
              ? savingEl.dataset.savingTemplate
                  .replace('{{amount}}', this.formatMoney(savingsCents))
                  .replace('{{percent}}', `${discountPercent}%`)
              : '';
        }
      });
    }

    updateStepTileReadouts() {
      // Build selected map for options
      const selectedMap = {};
      this.optionSelects.forEach((selectEl) => {
        const optName = (selectEl.dataset.optionName || '').toLowerCase().trim();
        const hiddenInput = selectEl.querySelector('[data-option-input]');
        const val = hiddenInput ? hiddenInput.value : '';
        if (optName) {
          selectedMap[optName] = val;
        }
      });

      this.stepInputs.forEach((radio) => {
        const handle = radio.dataset.stepHandle;
        const id = radio.dataset.stepProductId;
        const stepProd = this.stepProducts[handle] || this.stepProducts[id];
        if (!stepProd || !stepProd.variants) return;

        const tile = radio.closest('.bundle-step-tile') || radio.closest('.bundle-tiles__tile');

        let matched = null;
        if (stepProd.options && stepProd.options.length > 0) {
          matched = stepProd.variants.find((v) => {
            return stepProd.options.every((name, index) => {
              const key = name.toLowerCase().trim();
              let targetVal = selectedMap[key];
              if (!targetVal) {
                const matchedKey = Object.keys(selectedMap).find(
                  (k) => k.startsWith(key) || key.startsWith(k)
                );
                if (matchedKey) targetVal = selectedMap[matchedKey];
              }
              return !targetVal || v.options[index] === targetVal;
            });
          });
        }
        if (!matched) {
          matched = stepProd.variants.find((v) => v.available) || stepProd.variants[0];
        }

        if (matched) {
          if (tile) {
            const priceEl = tile.querySelector('[data-step-price]');
            if (priceEl) priceEl.textContent = this.formatMoney(matched.price);
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
